import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { joinWaitlist as postJoin } from './waitlistClient';
import { WAITLIST_COPY as COPY, YOUTUBE_URL } from './waitlistCopy';

const tok = (t) => `var(--dwlf-${t})`;
const MONO = '"IBM Plex Mono", monospace';
// Loose on purpose, as the API is: the address is only stored, so a stricter check would only refuse people.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Kept off-screen rather than hidden, so form-filling bots still see and fill it; people never do. */
const honeypotSx = { position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' };

/** What stops the form being sent, in the words shown to the person; null when nothing does. */
function problemWith({ email, consent }) {
  if (!EMAIL_SHAPE.test(email.trim())) return COPY.errors.email;
  if (!consent) return COPY.errors.consent;
  return null;
}

export function YouTubeLink() {
  return (
    <Box component="a" href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" sx={{ fontFamily: MONO, fontSize: '0.875rem', color: tok('accent'), textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      {COPY.youtube}
    </Box>
  );
}

/**
 * The waitlist sign-up (DWLF-584): email, optional interests, consent and the privacy line.
 * `initialEmail` pre-fills the address when we already know it (from a refused Google sign-in);
 * `place` says where on the site it is shown: landing, sign_in or refused.
 */
export default function WaitlistForm({ initialEmail = '', place, join = postJoin }) {
  const [email, setEmail] = useState(initialEmail);
  const [interests, setInterests] = useState([]);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState('');
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('editing');

  const toggleInterest = (value) => setInterests((now) => (now.includes(value) ? now.filter((v) => v !== value) : [...now, value]));

  const submit = async (event) => {
    event.preventDefault();
    const problem = problemWith({ email, consent });
    setError(problem);
    if (problem) return;
    setStatus('sending');
    try {
      await join({ email: email.trim(), interests, consent, place, website });
      setStatus('joined');
    } catch (e) {
      setError(e.message);
      setStatus('editing');
    }
  };

  if (status === 'joined') {
    return (
      <Box role="status" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography component="h2" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.5rem', fontWeight: 500, color: tok('text') }}>
          {COPY.joinedHeading}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.9rem', color: tok('ink-70') }}>{COPY.joinedBody}</Typography>
        <YouTubeLink />
      </Box>
    );
  }

  return (
    <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      <TextField label={COPY.emailLabel} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
      <Box component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
        <Typography component="legend" sx={{ fontFamily: MONO, fontSize: '0.85rem', color: tok('ink-70'), mb: 0.5 }}>
          {COPY.interestsLabel}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
          {COPY.interests.map(({ value, label }) => (
            <FormControlLabel key={value} control={<Checkbox checked={interests.includes(value)} onChange={() => toggleInterest(value)} />} label={label} />
          ))}
        </Box>
      </Box>
      <FormControlLabel
        control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
        label={<Typography sx={{ fontSize: '0.9rem', color: tok('text') }}>{COPY.consent}</Typography>}
        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
      />
      <Box aria-hidden="true" sx={honeypotSx}>
        <label>
          Website
          <input name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </label>
      </Box>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: tok('ink-50') }}>
        {COPY.privacyBefore}
        <Box component="a" href="/privacy" target="_blank" rel="noopener" sx={{ color: tok('accent') }}>
          {COPY.privacyLink}
        </Box>
        {COPY.privacyAfter}
      </Typography>
      {error && (
        <Typography role="alert" sx={{ color: tok('down'), fontSize: '0.9rem' }}>
          {error}
        </Typography>
      )}
      <Button type="submit" variant="contained" disabled={status === 'sending'} sx={{ alignSelf: 'flex-start', minHeight: 48, px: 3 }}>
        {status === 'sending' ? COPY.submitting : COPY.submit}
      </Button>
      <YouTubeLink />
    </Box>
  );
}
